import React from "react";

import { Grid, GridSize } from "@mui/material";
import { useTranslation } from "react-i18next";

import Checkbox from "../../../../../../../components/Form/Checkbox";
import PhoneInput from "../../../../../../../components/Form/PhoneInput";
import { ShipmentsTracking } from "../../../../../../../models/shipments/shipments.tracking";
import { CommunicationsFromOptions } from "../../../../../../../util/enums";
import { shipmentStatus } from "../../../../../../../util/shipmentStatus";
import useStyles, {
  ContainerDetailsWrapper,
  ContainerDetailsArea,
  ContainerDetailsLabel,
  CustomerDetailsValue,
} from "./styles";

interface ShipmentTrackingDetailsViewProps {
  shipmentTracking?: ShipmentsTracking;
}

const ShipmentTrackingDetailsView = ({ shipmentTracking }: ShipmentTrackingDetailsViewProps) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const renderLabel = (label: string, value?: string, grid?: GridSize) => (
    <Grid item xs={12} md={grid ?? 3} lg={grid ?? 3} className={classes.labelContainer}>
      <label className={classes.label}>{label}</label>
      <span className={value && value.length ? classes.span : classes.emptySpan}>
        {value && value.length ? value : translate("empty")}
      </span>
    </Grid>
  );

  const renderCommunicationValue = (from: string) => {
    switch (from.toLowerCase()) {
      case CommunicationsFromOptions.EMAIL:
      case CommunicationsFromOptions.SMS:
        return <span className={classes.tags}>{from}</span>;
      case CommunicationsFromOptions.BOTH:
        return (
          <div className={classes.tagWrapper}>
            {[CommunicationsFromOptions.EMAIL, CommunicationsFromOptions.SMS].map((type) => (
              <span className={classes.tags} key={type}>
                {type}
              </span>
            ))}
          </div>
        );
      default:
        return <span className={classes.emptySpan}>{translate("empty")}</span>;
    }
  };

  const renderCustomerDetails = () => {
    if (shipmentTracking?.customersToShipment) {
      return (
        <div className={classes.mt20}>
          <span className={classes.titleSubContainer}>{translate("customerDetails")}</span>

          <Grid container direction="column" className={classes.mt20} spacing={2}>
            {shipmentTracking?.customersToShipment?.map((customerDetails, index: number) => {
              if (customerDetails.customer) {
                const { customer } = customerDetails;
                const customerCommunication = shipmentTracking?.communications?.find(
                  (communication) => communication.customerId === customer.id
                );

                return (
                  <ContainerDetailsWrapper key={index}>
                    <ContainerDetailsArea className="order">
                      <ContainerDetailsLabel>{translate("order")}</ContainerDetailsLabel>
                      <CustomerDetailsValue
                        isEmpty={!customerDetails.order || !customerDetails.order.length}
                      >
                        {customerDetails.order && customerDetails.order.length
                          ? customerDetails?.order
                          : translate("empty")}
                      </CustomerDetailsValue>
                    </ContainerDetailsArea>

                    <ContainerDetailsArea className="firstName">
                      <ContainerDetailsLabel>{translate("firstName")}</ContainerDetailsLabel>
                      <CustomerDetailsValue isEmpty={!customer.name || !customer.name.length}>
                        {customer.name && customer.name.length ? customer.name : translate("empty")}
                      </CustomerDetailsValue>
                    </ContainerDetailsArea>

                    <ContainerDetailsArea className="lastName">
                      <ContainerDetailsLabel>{translate("lastName")}</ContainerDetailsLabel>
                      <CustomerDetailsValue
                        isEmpty={!customer.lastName || !customer.lastName.length}
                      >
                        {customer.lastName && customer.lastName.length
                          ? customer.lastName
                          : translate("empty")}
                      </CustomerDetailsValue>
                    </ContainerDetailsArea>

                    <ContainerDetailsArea className="email">
                      <ContainerDetailsLabel>{translate("email")}</ContainerDetailsLabel>
                      <CustomerDetailsValue isEmpty={!customer.email || !customer.email.length}>
                        {customer.email && customer.email.length
                          ? customer.email
                          : translate("empty")}
                      </CustomerDetailsValue>
                    </ContainerDetailsArea>

                    <ContainerDetailsArea className="phone">
                      <PhoneInput
                        label={translate("phone")}
                        value={customer?.phone}
                        isLabelStyle
                        disabled
                        className={classes.phoneInput}
                        labelStyle={classes.label}
                      />
                    </ContainerDetailsArea>

                    <ContainerDetailsArea className="contactVia">
                      <ContainerDetailsLabel>{translate("contactVia")}</ContainerDetailsLabel>
                      {renderCommunicationValue(customerCommunication?.from ?? translate("empty"))}
                    </ContainerDetailsArea>
                  </ContainerDetailsWrapper>
                );
              }

              return null;
            })}
          </Grid>
        </div>
      );
    }

    return null;
  };

  const renderCommunications = () => {
    const usersCommunications = shipmentTracking?.communications?.filter(
      (communication) => !communication.customerId
    );

    if (usersCommunications && usersCommunications.length) {
      return (
        <div className={classes.mt20}>
          <span className={classes.titleSubContainer}>{translate("communications")}</span>
          <Grid container direction="column" className={classes.mt20} spacing={2}>
            {usersCommunications.map((communication) => {
              const { user } = communication;

              if (user) {
                const fullName = `${user.name ?? ""} ${user.lastName ?? ""}`;
                const from =
                  communication.from && communication.from.length
                    ? communication.from
                    : CommunicationsFromOptions.EMPTY;

                return (
                  <Grid item container direction="row">
                    {renderLabel(translate("user"), fullName)}
                    <Grid item xs={12} md={2} lg={2} className={classes.labelContainer}>
                      <label className={classes.label}>{translate("via")}</label>
                      {renderCommunicationValue(from)}
                    </Grid>
                  </Grid>
                );
              }

              return null;
            })}
          </Grid>
        </div>
      );
    }

    return null;
  };

  const renderPartner = () => {
    const agent = shipmentTracking?.shipmentAgents?.[0]?.agent || null;

    const getDataAgent = () => {
      if (agent?.entity && agent?.email) return `${agent.entity.trim()} - ${agent.email.trim()}`;
      if (agent?.email) return agent.email.trim();
      if (agent?.entity) return agent.entity.trim();
    };

    return (
      <div className={classes.mt20}>
        <span className={classes.titleSubContainer}>{translate("agent")}</span>
        <div className={classes.checkboxPartner}>
          <Checkbox
            name="hasPartner"
            label={translate("thisShipmentHasAnAgent")}
            checked={!!agent}
            disabled={true}
            labelDisabled={!agent}
          />
        </div>
        {agent ? (
          <Grid container direction="column" className={classes.mt20} spacing={2}>
            <ContainerDetailsWrapper key={agent?.id}>
              <ContainerDetailsArea className={classes.grindPartner}>
                <ContainerDetailsLabel>{translate("agent")}</ContainerDetailsLabel>
                <CustomerDetailsValue isEmpty={!agent}>{getDataAgent()}</CustomerDetailsValue>
              </ContainerDetailsArea>
            </ContainerDetailsWrapper>
          </Grid>
        ) : null}
      </div>
    );
  };

  return (
    <main>
      <div className="general">
        <span className={classes.titleSubContainer}>{translate("general")}</span>
        <Grid container direction="row" className={classes.mt20}>
          <Grid item container lg={12}>
            {renderLabel(translate("type"), shipmentTracking?.shipmentType)}
            {renderLabel(translate("containerNumber"), shipmentTracking?.containerNumbers)}
            {renderLabel(
              translate("status"),
              shipmentStatus(shipmentTracking?.shipmentTracking?.shipmentStatus)
            )}
          </Grid>
          <Grid item container lg={12} className={classes.mt20}>
            {renderLabel(`${translate("shipment")} #`, shipmentTracking?.containerId)}
            {renderLabel(translate("origin"), shipmentTracking?.shipmentTracking?.originPort)}
            {renderLabel(translate("destination"), shipmentTracking?.shipmentTracking?.finalPort)}
          </Grid>
          <Grid item container lg={12} className={classes.mt20}>
            {renderLabel(`${translate("carrierScac")} #`, shipmentTracking?.carrierScac)}
            {renderLabel(`${translate("booking")} #`, shipmentTracking?.bookingNo)}
            {renderLabel(translate("billOfLanding"), shipmentTracking?.billOfLanding)}
          </Grid>
        </Grid>
      </div>
      {renderPartner()}

      <div className={classes.mt20}>{renderCustomerDetails()}</div>

      {renderCommunications()}
    </main>
  );
};

export default ShipmentTrackingDetailsView;
